import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaTrash } from "react-icons/fa";
import { AuthContext } from "../../../context/AuthContext";
import "./Cart.css";
import { BACKEND_URL } from "../../../config";

const CURRENCY = "€";

// NAYA: yeh 2 product names hamesha "You may also like" me sabse pehle fixed rahenge
const FIXED_RELATED_NAMES = ["Super Kamagra", "Kamagra Oral Jelly"];

function Cart() {
  const navigate = useNavigate();
  const { user, setShowAuth } = useContext(AuthContext);
  const [cartItems, setCartItems] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(savedCart);
  }, []);

  useEffect(() => {
    if (cartItems.length === 0) {
      setRelatedProducts([]);
      return;
    }

    fetch(`${BACKEND_URL}/api.php?module=product`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) return;

        const allProducts = data.data || [];

        const cartCategories = new Set(
          cartItems.map((item) => item.categoryName).filter(Boolean)
        );

        const cartProductIds = new Set(
          cartItems.map((item) => item.id)
        );

        // NAYA: fixed products ko naam se dhundo, jo cart me already na hon
        const fixedProducts = FIXED_RELATED_NAMES
          .map((name) =>
            allProducts.find(
              (p) =>
                p.productName?.toLowerCase() === name.toLowerCase() &&
                !cartProductIds.has(p.id)
            )
          )
          .filter(Boolean); // agar koi naam match na ho to skip ho jayega

        const fixedIds = new Set(fixedProducts.map((p) => p.id));

        const matches = allProducts.filter(
          (p) =>
            cartCategories.has(p.categoryName) &&
            !cartProductIds.has(p.id) &&
            !fixedIds.has(p.id) // NAYA: fixed products dobara list me na aayein
        );

        // Baaki related products shuffle honge, fixed wale order me upar rahenge
        const shuffled = matches.sort(() => 0.5 - Math.random());

        // NAYA: fixed products ko hamesha shuru me jodo
        setRelatedProducts([...fixedProducts, ...shuffled]);
      })
      .catch((err) => console.log(err));
  }, [cartItems]);

  const updateCart = (updatedCart) => {
    setCartItems(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const removeItem = (index) => {
    const updated = [...cartItems];
    updated.splice(index, 1);
    updateCart(updated);
  };

  const changeQty = (index, type) => {
    const updated = [...cartItems];
    if (type === "plus") {
      updated[index].qty += 1;
    } else {
      if (updated[index].qty > 1) updated[index].qty -= 1;
    }
    updateCart(updated);
  };

  const safeJSON = (data) => {
    try {
      return typeof data === "string" ? JSON.parse(data) : data || [];
    } catch {
      return [];
    }
  };

  const addRelatedToCart = (product) => {
    const pack = safeJSON(product.packPricing)?.[0];
    if (!pack) return;

    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const item = {
      id: product.id,
      productName: product.productName,
      categoryName: product.categoryName,
      image: product.image?.startsWith("http")
        ? product.image
        : `${BACKEND_URL}/${product.image}`,
      packSize: `${pack.packSize} ${pack.unit}`,
      price: Number(pack.price),
      qty: 1
    };

    const existingIndex = cart.findIndex(
      (c) => c.id === item.id && c.packSize === item.packSize
    );

    if (existingIndex > -1) {
      cart[existingIndex].qty += 1;
    } else {
      cart.push(item);
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    setCartItems(cart);
  };

  const total = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);

  // Gate checkout behind login. The cart stays fully visible/usable
  // whether logged in or not — only "Proceed to Checkout" checks, so
  // the user finds out immediately instead of failing at the last
  // step on the payment page.
  const handleCheckoutClick = () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    navigate("/checkout", { state: { cartItems, total } });
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="empty-cart">
          <h2>Your cart is empty</h2>
          <p>Add products to continue shopping</p>
          <Link to="/" className="shop-btn">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (

    <>
     {/* SEO */}
        <Helmet>
          <title>Your Cart | RealGenericShop</title>
          <meta
            name="description"
            content="View items in your shopping cart and proceed to checkout."
          />
          <link rel="canonical" href="https://realgenericshop.com/cart" />
        </Helmet>

    <div className="cart-page">

      <h1 className="cart-title">Shopping Cart</h1>

      <div className="cart-wrapper">

        {/* ================= LEFT ================= */}
        <div className="cart-left">

          <div className="cart-card">

            <div className="cart-header-row">
              <span>Your cart</span>
              <Link to="/" className="continue-shopping-link">
                Continue Shopping
              </Link>
            </div>

            {cartItems.map((item, index) => (
              <div className="cart-item" key={index}>

                {/* IMAGE */}
                <div className="cart-img-box">
                  <img src={item.image} alt={item.productName} />
                </div>

                {/* MIDDLE - name, pack, qty */}
                <div className="cart-details">
                  <div className="cart-top-row">
                    <h3>{item.productName}</h3>
                    <span className="cart-price-col">
                      {CURRENCY}{(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>

                  <p className="cart-pack">{item.packSize}</p>

                  <div className="cart-bottom-row">
                    <div className="qty-box">
                      <button className="qty-btn" onClick={() => changeQty(index, "minus")}>-</button>
                      <span className="qty-number">{item.qty}</span>
                      <button className="qty-btn" onClick={() => changeQty(index, "plus")}>+</button>
                    </div>

                    <button className="remove-btn" onClick={() => removeItem(index)}>
                      <FaTrash />
                    </button>
                  </div>
                </div>

              </div>
            ))}

          </div>

          {/* ================= RELATED PRODUCTS — separate card ================= */}
          {relatedProducts.length > 0 && (
            <div className="cart-related-card-wrapper">
              <h3 className="cart-related-title">You may also like</h3>

              <div className="cart-related-scroll">
                {relatedProducts.map((p) => {
                  const pack = safeJSON(p.packPricing)?.[0];
                  return (
                    <div className="cart-related-card" key={p.id}>
                      <img
                        src={
                          p.image?.startsWith("http")
                            ? p.image
                            : `${BACKEND_URL}/${p.image}`
                        }
                        alt={p.productName}
                        onClick={() => navigate(`/product/${p.slug}`)}
                      />
                      <h4 onClick={() => navigate(`/product/${p.slug}`)}>
                        {p.productName}
                      </h4>
                      <p className="cart-related-price">
                        {CURRENCY}{pack?.price || "N/A"}
                      </p>
                      <button
                        className="cart-related-add-btn"
                        onClick={() => addRelatedToCart(p)}
                      >
                        Add to Cart
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* ================= RIGHT ================= */}
        <div className="cart-summary">

          <h2>Order Summary</h2>

          <div className="summary-row">
            <span>Subtotal</span>
            <span>{CURRENCY}{total.toFixed(2)}</span>
          </div>

          <div className="summary-row">
            <span>Shipping</span>
            <span>{CURRENCY}20.00</span>
          </div>

          <div className="summary-row total">
            <span>Total</span>
            <span>{CURRENCY}{(total + 20).toFixed(2)}</span>
          </div>

          <button
            className="checkout-btn"
            onClick={handleCheckoutClick}
          >
            Proceed to Checkout
          </button>

        </div>

      </div>
    </div>
    </>
  );
}

export default Cart;
